import * as React from 'react';
import { Category, CategoryLink, ColumnCategories, MegaNavLink } from './megaNavLinks';
import { H6 } from '@liveauctioneers/caterwaul-components/lib/Text/Text';
import CategoryNavigationAd from '@/components/Ads/CategoryNavigationAd';
import Link from '@liveauctioneers/caterwaul-components/lib/Link/Link';
import styled from 'styled-components';

let columnKey = 0;

type Props = {
    active: boolean;
    closeNav: () => void;
    id: string;
    linkArray: MegaNavLink;
    mobile: string;
};

const LinkContainer = ({ active, closeNav, id, linkArray }: Props) => {
    const mapLinks = React.useCallback(
        ({ route, text }: CategoryLink) => {
            return (
                <li key={text}>
                    <Link onClick={closeNav} route to={route}>
                        {text}
                    </Link>
                </li>
            );
        },
        [closeNav]
    );

    const mapColumn = React.useCallback(
        ({ headerLink, headerText, links }: Category) => {
            const title = headerLink ? (
                <Link route to={headerLink}>
                    {headerText}
                </Link>
            ) : (
                <div>{headerText}</div>
            );

            return (
                <StyleSectionContainer key={headerText}>
                    <StyledHeaderTitle>{title}</StyledHeaderTitle>
                    <ul>{links.map(mapLinks)}</ul>
                </StyleSectionContainer>
            );
        },
        [mapLinks]
    );

    const mapLinkObjects = React.useCallback(
        ({ columnCategories }: ColumnCategories) => {
            columnKey += 1;
            return (
                <StyledColumn key={columnKey}>
                    <StyledWrapper>{columnCategories.map(mapColumn)}</StyledWrapper>
                </StyledColumn>
            );
        },
        [mapColumn]
    );

    const generateColumns = React.useCallback(
        (linkObjects: MegaNavLink) => {
            return <StyledColumnContainer>{linkObjects.map(mapLinkObjects)}</StyledColumnContainer>;
        },
        [mapLinkObjects]
    );

    const adId = id.replace('navigation.', '');

    if (!active) {
        return null;
    }

    return (
        <StyledMegaNav>
            <StyledSeo>
                {generateColumns(linkArray)}
                <CategoryNavigationAd adId={adId} closeNav={closeNav} />
            </StyledSeo>
        </StyledMegaNav>
    );
};

export default LinkContainer;

const StyledMegaNav = styled.div`
    position: absolute;
    left: calc(50% - 602px);
    top: 54px;
    width: 1204px;
    min-height: 388px;
    z-index: -1;
    background-color: ${({ theme }) => theme.colors.white};
    border: 1px solid ${({ theme }) => theme.colors.grey400};
    box-shadow: 1px 1px 3px 0 ${({ theme }) => theme.colors.shadow200};

    @media (max-width: ${({ theme }) => theme.breakpoints.pageWidthNum - 1}px) {
        left: calc(50% - 384px);
        width: ${({ theme }) => theme.breakpoints.tabletNarrowWidth};
        min-height: 290px;
    }

    @media (max-width: 975px) {
        top: 47px;
    }
`;

const StyledSeo = styled.div`
    width: 100%;
    display: flex;
    justify-content: center;
`;

const StyledColumnContainer = styled.div`
    flex: 1 1 auto;
    display: flex;
    flex-flow: row wrap;
    padding: 0 0 16px;

    ul {
        list-style: none;
        padding: 0;
        margin: 0;

        & > li {
            line-height: 20px;
            margin-top: 8px;

            & > a {
                overflow: unset;
                font-size: 14px;
                line-height: 20px;
                color: ${({ theme }) => theme.colors.grey100};
                font-family: ${({ theme }) => theme.typography.fontPrimary};
                text-decoration: none;

                &:hover {
                    text-decoration: underline;
                }
            }
        }
    }
`;

const StyledColumn = styled.div`
    text-align: left;
    display: flex;
    width: 200px;
    margin-left: 32px;
`;

const StyledWrapper = styled.div`
    white-space: pre-wrap;
    width: 100%;
    margin-top: 16px;
`;

const StyleSectionContainer = styled.div`
    &:not(:first-child) {
        margin-top: 16px;
    }
`;

const StyledHeaderTitle = styled(H6)`
    && {
        color: ${({ theme }) => theme.colors.black};
        line-height: 20px;
        padding: 0;
        display: inline-block;

        a {
            text-decoration: none;
            color: ${({ theme }) => theme.colors.black};

            &:hover {
                text-decoration: none;
                color: ${({ theme }) => theme.colors.black};
            }
        }
    }
`;
